// src/hooks/useOpportunitySearch.ts

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems } from '../support/queries';
import { CacheKey } from '../types/cache-keys';

export interface OpportunitySearchResult {
  id: string;
  name: string;
}

/**
 * Server-side search on Salesforce Opportunity Name, the opportunity twin of `useCustomerSearch`.
 * Debounced, POSTs to `sf/opportunity` with a `search_string` (the same body shape the opportunity
 * list uses), and returns `{id, name}[]` for a typeahead. An 18-char Salesforce id typed directly is
 * passed through as the search string too, so a lookup by id works without a separate code path.
 */
export const useOpportunitySearch = () => {
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState(term);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedTerm(term.trim());
    }, 400);
    return () => clearTimeout(handle);
  }, [term]);

  const query = useQuery<OpportunitySearchResult[]>({
    queryKey: [CacheKey.Opportunity, 'name-search', debouncedTerm],
    queryFn: async () => {
      if (!debouncedTerm) return [];
      const body = {
        filters: [],
        order_by: 'Name ASC',
        columns: ['Id', 'Name'],
        search_string: debouncedTerm,
      };
      const resp = await fetchItems(
        'sf/opportunity',
        { page: 1, size: 25 },
        { pagination: true, body },
      );
      const items = resp.items ?? resp.records ?? resp ?? [];
      return items.map((o: any) => ({
        id: o.Id ?? o.id,
        name: o.Name ?? o.name ?? (o.Id ?? o.id),
      }));
    },
    ...{
        enabled: !!debouncedTerm,
        placeholderData: keepPreviousData,
      },
  });

  return {
    term,
    setTerm,
    results: query.data ?? [],
    isLoading: query.isLoading,
  };
};
